/**
 * Sprint 11.4 Phase 2 — Resume Enrichment Workspace.
 *
 * Focused, dedicated screen whose ONLY job is to make an AI-extracted
 * standardised resume publication-ready. Lives at
 *   /portal/resume-enrichment/:candidateId
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  ← Back to queue · Candidate name · status pill   [Approve & finish]│
 *   ├─────────────────────────────────────────────────────────────────────┤
 *   │  Source pane             │     Standardised sections editor          │
 *   │  - Open original PDF     │     - per section: edit + Refine with AI  │
 *   │  - Raw extracted text    │     - inline save on blur                 │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * Back navigation goes to the enrichment queue, never to the candidate page.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  ArrowLeft, CheckCircle2, ExternalLink, Sparkles,
} from 'lucide-react';
import { useResumeEnrichmentWorkspace } from '@/hooks/useResumeEnrichmentWorkspace';
import { ftime } from '@/lib/resumeEnrichmentErrors';
import { SECTION_ORDER } from '@/lib/resumeSectionConstants';
import {
  CandidateProfileCard,
  SourcePane,
  SectionEditor,
  ApproveButton,
  WorkspaceSkeleton,
  ErrorState,
} from '@/components/resumeEnrichment/EnrichmentWorkspaceParts';

export default function ResumeEnrichmentWorkspace() {
  const navigate = useNavigate();
  const {
    candidateId,
    evaluationId,
    candidateQ,
    resumeQ,
    c,
    r,
    profileEditAllowed,
    enrichAllowed,
    sectionEditAllowed,
    profileEditDenied,
    enrichEditDenied,
    profilePatchMut,
    approveMut,
    saveProfile,
    saveSection,
    approveResume,
  } = useResumeEnrichmentWorkspace();

  // --- Loading / error states ---------------------------------------------
  if (!evaluationId) {
    return (
      <ErrorState
        title="Evaluation required"
        message="Open Resume Enrichment from Candidate Detail with an evaluation selected (evaluation_id is required)."
        onBack={() => navigate('/portal/resume-enrichment')}
      />
    );
  }

  if (candidateQ.isLoading || resumeQ.isLoading) return <WorkspaceSkeleton />;

  if (candidateQ.isError) {
    return (
      <ErrorState
        title="Couldn't load candidate"
        message={candidateQ.error?.message}
        onBack={() => navigate('/portal/resume-enrichment')}
      />
    );
  }

  if (resumeQ.isError && resumeQ.error?.response?.status === 404) {
    return (
      <ErrorState
        title="No standardised resume yet"
        message="This candidate has no AI-generated resume to enrich. Generate one from the candidate's Resume tab first."
        onBack={() => navigate('/portal/resume-enrichment')}
        candidate={c}
      />
    );
  }

  // The GET returns 200 with body `null` when no resume exists.
  if (resumeQ.isSuccess && !r) {
    return (
      <ErrorState
        title="No standardised resume yet"
        message="This candidate has no AI-generated resume to enrich. Generate one from the candidate's Resume tab first."
        onBack={() => navigate('/portal/resume-enrichment')}
        candidate={c}
      />
    );
  }

  if (!r || !c) return <WorkspaceSkeleton />;

  const payload = r.payload || {};
  const isApproved = !!r.edited_at;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-5" data-testid="resume-enrichment-workspace">
        {/* ============== TOP BAR ============== */}
        <Card className="card-elevated">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex min-w-0 items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={() => navigate('/portal/resume-enrichment')}
                data-testid="back-to-queue"
              >
                <ArrowLeft className="h-4 w-4" /> Queue
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-lg font-semibold" data-testid="workspace-candidate-name">
                    {c.full_name}
                  </h1>
                  <Badge
                    variant={isApproved ? 'secondary' : 'outline'}
                    className={
                      isApproved
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                        : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                    }
                    data-testid="workspace-status-badge"
                  >
                    {isApproved ? (
                      <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approved</>
                    ) : (
                      <><Sparkles className="mr-1 h-3.5 w-3.5" /> Needs review</>
                    )}
                  </Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {c.role_label || c.role || '—'} · Generated {ftime(r.generated_at)}
                  {r.edited_at ? ` · Last edited ${ftime(r.edited_at)}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {profileEditAllowed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-muted-foreground"
                  onClick={() => navigate(
                    `/portal/candidates/${candidateId}?eval=${encodeURIComponent(evaluationId)}`,
                  )}
                  data-testid="open-candidate-page"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Candidate page
                </Button>
              )}
              {enrichAllowed && (
                <ApproveButton
                  onConfirm={approveResume}
                  pending={approveMut.isPending}
                  isApproved={isApproved}
                  canEdit={enrichAllowed}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* ============== CANDIDATE PROFILE CARD ============== */}
        <CandidateProfileCard
          candidate={c}
          onPatch={saveProfile}
          saving={profilePatchMut.isPending}
          canEdit={profileEditAllowed}
          showDeniedBanner={profileEditDenied}
          evaluationId={evaluationId}
        />

        {/* ============== SPLIT PANE ============== */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,_340px)_minmax(0,_1fr)]">
          {/* ---------- LEFT: SOURCE ---------- */}
          <SourcePane candidate={c} resume={r} />

          {/* ---------- RIGHT: EDITOR ---------- */}
          <div className="space-y-3" data-testid="sections-editor">
            {enrichEditDenied && (
              <div
                className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100"
                data-testid="enrichment-edit-permission-banner"
                role="status"
              >
                <p className="font-semibold">View only</p>
                <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-100/80">
                  You don’t have permission to edit resume sections.
                </p>
              </div>
            )}
            {SECTION_ORDER.map((key) => (
              <SectionEditor
                key={key}
                sectionKey={key}
                value={payload[key]}
                onSave={(value) => saveSection(key, value)}
                candidateId={candidateId}
                evaluationId={evaluationId}
                hasSourceFile={!!r.source_file_id}
                canEdit={sectionEditAllowed}
              />
            ))}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
